package com.evoapps.evoshift.ui.richtext

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.RichBlock
import com.evoapps.evoshift.domain.model.RichColor
import com.evoapps.evoshift.domain.model.RichDocument
import com.evoapps.evoshift.domain.model.RichMark
import com.evoapps.evoshift.domain.model.RichSize
import com.evoapps.evoshift.domain.model.RichSpan
import com.evoapps.evoshift.domain.model.RichStyle
import com.evoapps.evoshift.domain.model.RichText
import com.evoapps.evoshift.widgets.LocalOnColours

/*
 * SH-211: how a formatted lesson looks, the same on Android, iOS, the desktop and the web: one
 * Compose renderer (AnnotatedString per paragraph, list items as a row of marker and text). The
 * editor's field draws the same styles ([richSpanStyle], [richTextStyle]).
 */

/**
 * The lesson colours: deep shades on the light screens and cards, light ones on the dark
 * dialogs and cards, each at least 4.5:1 on every surface of all nine themes and on the
 * highlight (RichPaletteContrastTest).
 */
object RichPalette {
    fun color(color: RichColor, dark: Boolean): Color = if (dark) {
        when (color) {
            RichColor.RED -> Color(0xFFFFB4AB)
            RichColor.ORANGE -> Color(0xFFFFCC80)
            RichColor.GREEN -> Color(0xFFA5D6A7)
            RichColor.BLUE -> Color(0xFFA8C7FA)
            RichColor.PURPLE -> Color(0xFFE1BEE7)
            RichColor.GREY -> Color(0xFFCFD8DC)
        }
    } else {
        when (color) {
            RichColor.RED -> Color(0xFFA8231B)
            RichColor.ORANGE -> Color(0xFF8A4B00)
            RichColor.GREEN -> Color(0xFF1B6E20)
            RichColor.BLUE -> Color(0xFF0B57D0)
            RichColor.PURPLE -> Color(0xFF6A1B9A)
            RichColor.GREY -> Color(0xFF545454)
        }
    }

    /** The highlighter: a pale yellow on light surfaces, a dark amber on dark ones. */
    fun highlight(dark: Boolean): Color = if (dark) Color(0xFF4D3F00) else Color(0xFFFFF0A0)
}

/** What a span of these [styles] looks like ([dark]: on a dark surface). */
fun richSpanStyle(styles: Collection<RichStyle>, dark: Boolean, linkColor: Color): SpanStyle {
    val marks = styles.filterIsInstance<RichStyle.Mark>().map { it.mark }.toSet()
    val colour = styles.filterIsInstance<RichStyle.Colour>().firstOrNull()?.color
    val link = styles.any { it is RichStyle.Link }
    val decorations = buildList {
        if (RichMark.UNDERLINE in marks || link) add(TextDecoration.Underline)
        if (RichMark.STRIKETHROUGH in marks) add(TextDecoration.LineThrough)
    }
    return SpanStyle(
        color = colour?.let { RichPalette.color(it, dark) } ?: if (link) linkColor else Color.Unspecified,
        fontWeight = if (RichMark.BOLD in marks) FontWeight.Bold else null,
        fontStyle = if (RichMark.ITALIC in marks) FontStyle.Italic else null,
        textDecoration = if (decorations.isEmpty()) null else TextDecoration.combine(decorations),
        background = if (RichMark.HIGHLIGHT in marks) RichPalette.highlight(dark) else Color.Unspecified
    )
}

/** The text style of a paragraph of [size], from the theme's type scale (lessons read at bodyLarge). */
fun richTextStyle(size: RichSize, typography: Typography): TextStyle = when (size) {
    RichSize.SMALL -> typography.bodyMedium
    RichSize.NORMAL -> typography.bodyLarge
    RichSize.LARGE -> typography.titleLarge
    RichSize.HEADING -> typography.headlineSmall.copy(fontWeight = FontWeight.Bold)
}

/**
 * Applies [spans] to [builder]'s text from [offset]: each run of characters with the same styles
 * gets one combined [SpanStyle] (underline and strikethrough together); links ([clickable]) are
 * link annotations the reader can open.
 */
internal fun AnnotatedString.Builder.addRichSpans(spans: List<RichSpan>, length: Int, offset: Int, dark: Boolean, linkColor: Color, clickable: Boolean) {
    if (spans.isEmpty() || length == 0) return
    val cuts = (spans.flatMap { listOf(it.start, it.end) } + listOf(0, length)).map { it.coerceIn(0, length) }.distinct().sorted()
    cuts.zipWithNext().forEach { (a, b) ->
        if (a >= b) return@forEach
        val styles = spans.filter { it.start <= a && it.end >= b }.map { it.style }
        if (styles.isNotEmpty()) addStyle(richSpanStyle(styles, dark, linkColor), a + offset, b + offset)
    }
    if (clickable) spans.forEach { span ->
        val link = span.style as? RichStyle.Link ?: return@forEach
        val start = span.start.coerceIn(0, length)
        val end = span.end.coerceIn(0, length)
        if (start < end) {
            addLink(
                LinkAnnotation.Url(link.url, TextLinkStyles(style = SpanStyle(color = linkColor, textDecoration = TextDecoration.Underline))),
                start + offset, end + offset
            )
        }
    }
}

/** One paragraph's text with its styles; links open in the browser ([LinkAnnotation.Url]). */
fun richParagraph(block: RichBlock, dark: Boolean, linkColor: Color): AnnotatedString = buildAnnotatedString {
    append(block.text)
    // Links only with a safe address (RichText.sanitize already dropped the others).
    val spans = block.spans.filter { it.style !is RichStyle.Link || RichText.safeLink((it.style as RichStyle.Link).url) != null }
    addRichSpans(spans, block.text.length, 0, dark, linkColor, clickable = true)
}

/**
 * A formatted lesson as the learner sees it. Paragraphs in their size; list items indented by
 * level with their marker ("1.", "a)", "•") in a column of their own, so wrapped lines align.
 */
@Composable
fun RichTextView(document: RichDocument, modifier: Modifier = Modifier, color: Color = Color.Unspecified) {
    val on = LocalOnColours.current
    val typography = MaterialTheme.typography
    val markers = remember(document) { RichText.markers(document) }
    val paragraphs = remember(document, on) { document.blocks.map { richParagraph(it, on.dark, on.link) } }
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        document.blocks.forEachIndexed { index, block ->
            val style = richTextStyle(block.size, typography)
            val marker = markers[index]
            if (marker == null) {
                Text(paragraphs[index], style = style, color = color, modifier = Modifier.fillMaxWidth())
            } else {
                Row(Modifier.fillMaxWidth()) {
                    Spacer(Modifier.width(ListIndent * block.level))
                    Text(marker, style = style, color = color, textAlign = TextAlign.End, modifier = Modifier.widthIn(min = MarkerWidth))
                    Spacer(Modifier.width(8.dp))
                    Text(paragraphs[index], style = style, color = color, modifier = Modifier.weight(1f))
                }
            }
        }
    }
}

private val ListIndent = 24.dp
private val MarkerWidth = 20.dp
